import {
  Button,
  Card,
  LifetimeDiscountBadge,
  ToggleGroup,
  ToggleGroupItem,
  useToast,
} from '@nfp-tracker/components';
import { Check, Crown, Loader2, RefreshCcw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ROUTES } from '../constants/routes';
import { useBillingPlans } from '../hooks/useBillingPlans';
import { useOwnerContext } from '../hooks/useOwnerContext';
import { useSubscription } from '../hooks/useSubscription';
import { useSubscriptionStorefrontAccess } from '../hooks/useSubscriptionManagementAvailability';
import { supabase } from '../integrations/supabase/client';
import { trackEvent } from '../integrations/analytics/client';
import { invokeEdgeFunction } from '../integrations/supabase/functions';
import {
  APPLE_BILLING_PROVIDER,
  APPLE_PRODUCT_IDS_BY_PLAN_KEY,
  APPLE_PLAN_KEY_BY_PRODUCT_ID,
  type CheckoutSessionRequest,
  type PortalSessionRequest,
  PRIVACY_POLICY_URL,
  STRIPE_DEFAULT_TRIAL_DAYS,
  STRIPE_SUBSCRIPTION_PLAN_KEYS,
  type StripeSubscriptionPlanKey,
  SUPPORT_EMAIL,
  SUPPORT_MAILTO_URL,
  TERMS_OF_USE_URL,
} from '@nfp-tracker/utils';
import type { BillingPlanResponse } from '@nfp-tracker/utils';
import {
  getIosCurrentEntitlements,
  getIosSubscriptionProducts,
  isNativeIosPurchasesAvailable,
  type AppStoreSubscriptionProduct,
  type AppStoreTransaction,
  presentIosManageSubscriptions,
  purchaseIosSubscription,
  restoreIosSubscriptions,
} from '../plugins/appStorePurchases';
import { handleExternalError } from '../utils/errorSnackbar';
import { syncAppStoreTransactions } from '../utils/appleSubscriptionSync';
import { buildBillingReturnBaseUrl } from '../utils/authRedirect';
import {
  formatCurrencyFromMinorUnits,
  isCheckoutPlanKey,
  openExternalUrl,
  resolvePlanAction,
  type CheckoutState,
} from '../utils/subscriptionPage';
import { deriveSubscriptionDateMessaging } from '../utils/subscriptionStatus';

type PlanRecord = Record<StripeSubscriptionPlanKey, BillingPlanResponse>;
type ApplePlanRecord = Record<StripeSubscriptionPlanKey, AppStoreSubscriptionProduct>;

type PlanCardDetails = {
  displayName: string;
  priceDisplay: string;
  interval: 'month' | 'year';
  unitAmount: number;
  currency: string;
  basePriceDisplay?: string;
  discountApplied?: boolean;
  yearlySavingsLabel: string | null;
};

const normalizeStripePlanDetails = (
  plan: BillingPlanResponse | null
): PlanCardDetails | null => {
  if (!plan) {
    return null;
  }

  return {
    displayName: plan.displayName,
    priceDisplay: plan.priceDisplay,
    interval: plan.interval,
    unitAmount: plan.unitAmount,
    currency: plan.currency,
    basePriceDisplay: plan.basePriceDisplay,
    discountApplied: plan.discountApplied,
    yearlySavingsLabel: plan.yearlySavingsLabel,
  };
};

const normalizeApplePlanDetails = (
  product: AppStoreSubscriptionProduct | null
): PlanCardDetails | null => {
  if (!product || !product.subscriptionPeriodUnit || !product.subscriptionPeriodValue) {
    return null;
  }

  const interval = product.subscriptionPeriodUnit === 'year' ? 'year' : 'month';

  return {
    displayName: product.displayName,
    priceDisplay: product.displayPrice,
    interval,
    unitAmount: Math.round((product.price ?? 0) * 100),
    currency: product.currencyCode ?? 'USD',
    yearlySavingsLabel: null,
  };
};

const getAnnualMonthlyBreakdown = (
  selectedPlan: StripeSubscriptionPlanKey,
  details: PlanCardDetails | null
): string | null => {
  if (selectedPlan !== STRIPE_SUBSCRIPTION_PLAN_KEYS.annual || !details) {
    return null;
  }

  return formatCurrencyFromMinorUnits(
    details.unitAmount / 12,
    details.currency
  );
};

const SUBSCRIPTION_FEATURES = [
  'Unlimited cycle tracking',
  'Optional partner collaboration',
  'Daily reminders',
  'Cycle summaries',
  'Export your data',
] as const;

const Subscription = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [userId, setUserId] = useState<string | null>(null);
  const [actorRole, setActorRole] = useState<'tracking' | 'viewing'>('tracking');
  const [checkingOut, setCheckingOut] = useState<CheckoutState>(null);
  const [selectedPlan, setSelectedPlan] = useState<StripeSubscriptionPlanKey>(STRIPE_SUBSCRIPTION_PLAN_KEYS.annual);
  const [didApplyInitialPlanSelection, setDidApplyInitialPlanSelection] = useState(false);
  const [applePlans, setApplePlans] = useState<ApplePlanRecord | null>(null);
  const [appleLoading, setAppleLoading] = useState(false);
  const [appleProductsError, setAppleProductsError] = useState(false);
  const [syncingApple, setSyncingApple] = useState(false);
  const previousLivePlanKeyRef = useRef<StripeSubscriptionPlanKey | null>(null);
  const lastSyncedAppleTransactionRef = useRef<string | null>(null);
  const isNativeIos = isNativeIosPurchasesAvailable();
  const termsUrl = (import.meta.env.VITE_TERMS_URL as string | undefined) ?? TERMS_OF_USE_URL;
  const privacyUrl = (import.meta.env.VITE_PRIVACY_URL as string | undefined) ?? PRIVACY_POLICY_URL;

  // Shared, session-cached auth/owner/permission context — dedupes the owner-resolution
  // chain against whatever other page already warmed this cache, instead of re-resolving
  // the owner and re-querying user_subscriptions for the Stripe customer id from scratch.
  const ownerContextQuery = useOwnerContext();
  const ownerContext = ownerContextQuery.data;
  const couple = ownerContext?.status === 'ready' ? ownerContext.couple : null;
  const coupleId = couple?.id;
  const hasStripeCustomer = Boolean(couple?.stripe_customer_id);
  const loading = ownerContextQuery.isLoading;

  // `get-billing-plans` requires a user JWT, so it waits for the session to resolve rather than
  // firing on mount and 401-ing during auth rehydration. A failure here leaves the plan cards in
  // their loading state, which is already handled below — it is not worth a toast.
  const billingPlans = useBillingPlans({
    enabled: ownerContext?.status === 'ready',
  }).data;
  const plans: PlanRecord | null = billingPlans?.planRecord ?? null;
  const hasFounderPricing = billingPlans?.hasFounderPricing ?? false;

  const {
    status,
    daysRemaining,
    trialEndDate,
    subscriptionEndDate,
    subscriptionRenewalDate,
    subscriptionAutoRenews,
    freeForLife,
    planKey: currentPlanKey,
    stripePriceId: currentStripePriceId,
    billingProvider,
    appStoreProductId,
    loading: subscriptionLoading,
  } = useSubscription(coupleId);
  const {
    isSubscriptionManagementAvailable,
    isExternalPricingAvailable,
  } = useSubscriptionStorefrontAccess();

  const isViewingUser = actorRole === 'viewing';
  const trialDayLabel = `${STRIPE_DEFAULT_TRIAL_DAYS}-day`;
  const isPaidActive = status === 'active';
  const normalizedStatus = status ?? 'inactive';
  const isInactiveStatus = normalizedStatus === 'inactive' || normalizedStatus === 'canceled';
  const currentPaidPlanKey = isCheckoutPlanKey(currentPlanKey ?? '') ? currentPlanKey : null;
  const selectedStripePlanDetails = normalizeStripePlanDetails(plans?.[selectedPlan] ?? null);
  const selectedApplePlanDetails = normalizeApplePlanDetails(applePlans?.[selectedPlan] ?? null);
  const currentPlanDetails = useMemo(() => {
    if (billingProvider === APPLE_BILLING_PROVIDER && appStoreProductId && applePlans) {
      const matched = Object.values(applePlans).find((plan) => plan.productId === appStoreProductId) ?? null;
      return normalizeApplePlanDetails(matched);
    }

    if (!plans) return null;
    return Object.values(plans).find((plan) => plan.stripePriceId === currentStripePriceId) ?? null;
  }, [applePlans, appStoreProductId, billingProvider, currentStripePriceId, plans]);
  const currentPlanLabel = freeForLife
    ? 'Free for Life'
    : currentPlanDetails?.displayName
      ?? (currentPlanKey === STRIPE_SUBSCRIPTION_PLAN_KEYS.monthly
        ? 'Monthly'
        : currentPlanKey === STRIPE_SUBSCRIPTION_PLAN_KEYS.annual
          ? 'Annual'
          : 'Not set');
  const currentPriceLabel = freeForLife
    ? 'No charge'
    : currentPlanDetails
      ? `${currentPlanDetails.priceDisplay}/${currentPlanDetails.interval}`
      : null;
  const currentBasePriceLabel =
    freeForLife || !currentPlanDetails?.basePriceDisplay
      ? null
      : `${currentPlanDetails.basePriceDisplay}/${currentPlanDetails.interval}`;
  const webAnnualMonthlyBreakdown = getAnnualMonthlyBreakdown(
    selectedPlan,
    selectedStripePlanDetails
  );
  const appleAnnualMonthlyBreakdown = getAnnualMonthlyBreakdown(
    selectedPlan,
    selectedApplePlanDetails
  );
  const formattedTrialEndDate = trialEndDate ? new Date(trialEndDate).toLocaleDateString() : null;
  const selectedIsCurrentPlan = Boolean(currentPaidPlanKey && selectedPlan === currentPaidPlanKey);
  const usingAppleBilling = billingProvider === APPLE_BILLING_PROVIDER;
  const hasLiveStripeSubscription =
    billingProvider === 'stripe'
    && (status === 'active' || status === 'trialing' || status === 'past_due');
  const canManageActiveAppleSubscription =
    usingAppleBilling
    && (status === 'active' || status === 'trialing' || status === 'past_due');

  const subscriptionStatusLabel = (() => {
    if (isViewingUser) return 'Managed by your partner';
    if (freeForLife) return 'Free for Life';
    if (status === 'active') return 'Active';
    if (status === 'trialing') return 'Trial';
    if (status === 'past_due') return 'Past Due';
    return 'Not Active';
  })();

  const subscriptionStatusDescription = (() => {
    if (isViewingUser) {
      return 'Subscriptions are managed by the tracking partner on this account.';
    }
    if (freeForLife) return 'Your lifetime access is active.';
    if (status === 'active') {
      return usingAppleBilling
        ? 'Your Apple subscription is active and in good standing.'
        : 'Your paid subscription is active and in good standing.';
    }
    if (status === 'trialing') {
      return `You have ${daysRemaining ?? 0} ${daysRemaining === 1 ? 'day' : 'days'} remaining in your free trial.`;
    }
    if (status === 'past_due') {
      return usingAppleBilling
        ? 'Apple reported a billing issue with this subscription.'
        : `Payment issue detected. ${daysRemaining ?? 0} ${daysRemaining === 1 ? 'day' : 'days'} remaining in grace period.`;
    }
    return 'No active subscription found.';
  })();

  const syncAppleTransactions = async (transactions: AppStoreTransaction[]) => {
    if (!transactions.length) {
      return {
        synced: false,
        billingTransitionWarning: null as string | null,
        accountMismatch: false,
      };
    }

    const uniqueTransactions = Array.from(
      new Map(transactions.map((transaction) => [transaction.transactionId, transaction])).values()
    );

    const sortedTransactions = [...uniqueTransactions].sort((left, right) => {
      const leftExpiry = left.expirationDate ? new Date(left.expirationDate).getTime() : 0;
      const rightExpiry = right.expirationDate ? new Date(right.expirationDate).getTime() : 0;
      return rightExpiry - leftExpiry;
    });

    const latest = sortedTransactions[0];
    if (!latest) {
      return {
        synced: false,
        billingTransitionWarning: null as string | null,
        accountMismatch: false,
      };
    }

    if (lastSyncedAppleTransactionRef.current === latest.transactionId) {
      return {
        synced: true,
        billingTransitionWarning: null as string | null,
        accountMismatch: false,
      };
    }

    setSyncingApple(true);
    try {
      const syncResult = await syncAppStoreTransactions(sortedTransactions, coupleId ?? undefined);
      lastSyncedAppleTransactionRef.current = syncResult.latestTransactionId;
      return {
        synced: syncResult.synced,
        billingTransitionWarning: syncResult.billingTransitionWarning,
        accountMismatch: syncResult.accountMismatch,
      };
    } catch (error: unknown) {
      handleExternalError(toast, error, {
        logPrefix: 'Error syncing App Store subscription:',
      });
      return {
        synced: false,
        billingTransitionWarning: null as string | null,
        accountMismatch: false,
      };
    } finally {
      setSyncingApple(false);
    }
  };

  const handleWebCheckout = async (planKey: StripeSubscriptionPlanKey) => {
    if (!userId || freeForLife) return;

    setCheckingOut(planKey);
    trackEvent('subscription_checkout_started', { planKey, channel: 'web' });

    try {
      const body: CheckoutSessionRequest = {
        planKey,
        returnUrlBase: buildBillingReturnBaseUrl(),
      };
      const { data, error } = await invokeEdgeFunction('create-checkout-session', {
        body,
      });

      if (error) throw error;

      if (data.url) {
        trackEvent('subscription_checkout_redirected', { planKey, channel: 'web' });
        openExternalUrl(data.url, undefined, { allowWebSameTabFallback: false });
      }
    } catch (error: unknown) {
      handleExternalError(toast, error, {
        logPrefix: 'Error creating checkout session:',
      });
    } finally {
      setCheckingOut(null);
    }
  };

  const handleAppleCheckout = async (planKey: StripeSubscriptionPlanKey) => {
    if (!coupleId || freeForLife || isViewingUser) return;

    setCheckingOut(planKey);
    try {
      const result = await purchaseIosSubscription(planKey, coupleId);

      if (result.status === 'cancelled') {
        trackEvent('ios_iap_purchase_cancelled', { planKey });
        return;
      }

      if (result.status === 'pending') {
        trackEvent('ios_iap_purchase_pending', { planKey });
        toast({
          title: 'Purchase pending',
          description: 'Apple is still processing your purchase. Access will update automatically.',
        });
        return;
      }

      const transactionsToSync = result.transaction
        ? [result.transaction, ...result.entitlements]
        : result.entitlements;
      const syncResult = await syncAppleTransactions(transactionsToSync);
      trackEvent('ios_iap_purchase_completed', {
        planKey,
        entitlementCount: result.entitlements.length,
        synced: syncResult.synced,
        accountMismatch: syncResult.accountMismatch,
        billingTransitionWarning: Boolean(syncResult.billingTransitionWarning),
      });

      if (syncResult.synced) {
        toast({
          title: 'Subscription updated',
          description: syncResult.billingTransitionWarning ?? 'Your Apple subscription is now active.',
        });
      } else if (syncResult.accountMismatch) {
        toast({
          title: 'Purchase detected',
          description: 'This device has receipts tied to a different app account. Please restore with the matching account or contact support.',
        });
      }
    } catch (error: unknown) {
      trackEvent('ios_iap_purchase_failed', { planKey });
      handleExternalError(toast, error, {
        logPrefix: 'Error completing App Store purchase:',
      });
    } finally {
      setCheckingOut(null);
    }
  };

  const handleRestorePurchases = async () => {
    setCheckingOut('portal');
    try {
      const entitlements = await restoreIosSubscriptions();
      const syncResult = await syncAppleTransactions(entitlements);
      trackEvent('ios_iap_restore_completed', {
        entitlementCount: entitlements.length,
        synced: syncResult.synced,
        accountMismatch: syncResult.accountMismatch,
        billingTransitionWarning: Boolean(syncResult.billingTransitionWarning),
      });
      toast({
        title: syncResult.synced
          ? 'Purchases restored'
          : syncResult.accountMismatch
            ? 'No matching purchases found'
            : 'No purchases found',
        description: syncResult.synced
          ? syncResult.billingTransitionWarning ?? 'Your App Store purchases were restored.'
          : syncResult.accountMismatch
            ? 'This device has App Store receipts, but none match this app account.'
            : 'We could not find an active App Store subscription to restore.',
      });
    } catch (error: unknown) {
      trackEvent('ios_iap_restore_failed');
      handleExternalError(toast, error, {
        logPrefix: 'Error restoring App Store purchases:',
      });
    } finally {
      setCheckingOut(null);
    }
  };

  const handleManageSubscription = async (
    options?: { targetPlanKey?: StripeSubscriptionPlanKey }
  ) => {
    trackEvent('subscription_manage_billing_clicked', {
      targetPlanKey: options?.targetPlanKey ?? selectedPlan,
      channel: isNativeIos && usingAppleBilling ? 'ios' : 'web',
    });

    if (isNativeIos && usingAppleBilling) {
      setCheckingOut('portal');
      try {
        await presentIosManageSubscriptions();
      } catch (error: unknown) {
        handleExternalError(toast, error, {
          logPrefix: 'Error opening App Store subscription management:',
        });
      } finally {
        setCheckingOut(null);
      }
      return;
    }

    setCheckingOut('portal');

    try {
      const body: PortalSessionRequest = {
        targetPlanKey: options?.targetPlanKey ?? selectedPlan,
        returnUrlBase: buildBillingReturnBaseUrl(),
      };
      const { data, error } = await invokeEdgeFunction(
        'create-portal-session',
        { body }
      );

      if (error) throw error;

      if (data.url) {
        openExternalUrl(data.url, undefined, { allowWebSameTabFallback: false });
      }
    } catch (error: unknown) {
      handleExternalError(toast, error, {
        logPrefix: 'Error creating portal session:',
      });
    } finally {
      setCheckingOut(null);
    }
  };

  const applePlanAction = (() => {
    if (isViewingUser) {
      return {
        label: 'Managed by partner',
        disabled: true,
        loading: false,
        onClick: () => undefined,
      };
    }

    if (freeForLife) {
      return {
        label: 'Included',
        disabled: true,
        loading: false,
        onClick: () => undefined,
      };
    }

    if (canManageActiveAppleSubscription) {
      return {
        label: 'Manage in App Store',
        disabled: false,
        loading: checkingOut === 'portal',
        onClick: () => {
          void handleManageSubscription();
        },
      };
    }

    return {
      label: 'Subscribe with Apple',
      disabled: !selectedApplePlanDetails || appleLoading || syncingApple,
      loading: checkingOut === selectedPlan,
      onClick: () => {
        void handleAppleCheckout(selectedPlan);
      },
    };
  })();

  const webPlanAction = (() => {
    if (canManageActiveAppleSubscription) {
      return {
        label: 'Managed by Apple',
        disabled: true,
        loading: false,
        onClick: () => undefined,
      };
    }

    if (!isNativeIos && usingAppleBilling) {
      if (isViewingUser) {
        return {
          label: 'Managed by partner',
          disabled: true,
          loading: false,
          onClick: () => undefined,
        };
      }

      if (freeForLife) {
        return {
          label: 'Included',
          disabled: true,
          loading: false,
          onClick: () => undefined,
        };
      }

      return {
        label: 'Manage in iPhone app',
        disabled: true,
        loading: false,
        onClick: () => undefined,
      };
    }

    if (isNativeIos && billingProvider !== 'stripe') {
      if (isViewingUser) {
        return {
          label: 'Managed by partner',
          disabled: true,
          loading: false,
          onClick: () => undefined,
        };
      }

      if (freeForLife) {
        return {
          label: 'Included',
          disabled: true,
          loading: false,
          onClick: () => undefined,
        };
      }

      if (hasStripeCustomer) {
        return {
          label: 'Manage Web Subscription',
          disabled: Boolean(checkingOut),
          loading: checkingOut === 'portal',
          onClick: () => {
            void handleManageSubscription({ targetPlanKey: selectedPlan });
          },
        };
      }

      return {
        label: 'Continue in Browser',
        disabled: Boolean(checkingOut),
        loading: checkingOut === selectedPlan,
        onClick: () => {
          void handleWebCheckout(selectedPlan);
        },
      };
    }

    return resolvePlanAction({
      isViewingUser,
      freeForLife,
      isInactiveStatus,
      hasStripeCustomer,
      selectedIsCurrentPlan: billingProvider === 'stripe' && selectedIsCurrentPlan,
      selectedPlan,
      checkingOut,
      onManageSubscription: (options) => {
        void handleManageSubscription(options);
      },
      onCheckout: (planKey) => {
        void handleWebCheckout(planKey);
      },
    });
  })();

  useEffect(() => {
    if (ownerContext?.status === 'unauthenticated') {
      navigate(ROUTES.auth);
    }
  }, [ownerContext, navigate]);

  useEffect(() => {
    if (ownerContext?.status !== 'ready') return;

    setUserId(ownerContext.actorUserId);

    const loadActorRole = async () => {
      const { data: account } = await supabase
        .from('user_accounts')
        .select('role')
        .eq('user_id', ownerContext.actorUserId)
        .maybeSingle();

      if (account?.role !== 'tracking') {
        setActorRole('viewing');
      }
    };

    void loadActorRole();
  }, [ownerContext]);

  const loadApplePlans = useCallback(async () => {
    if (!isNativeIos) return;

    setAppleLoading(true);
    setAppleProductsError(false);

    try {
      const products = await getIosSubscriptionProducts();
      const nextPlans: Partial<ApplePlanRecord> = {};
      const returnedProductIds: string[] = [];
      for (const product of products) {
        returnedProductIds.push(product.productId);
        const planKey = APPLE_PLAN_KEY_BY_PRODUCT_ID[product.productId];
        if (planKey) {
          nextPlans[planKey] = product;
        }
      }

      const missingPlanKeys = (Object.keys(APPLE_PRODUCT_IDS_BY_PLAN_KEY) as StripeSubscriptionPlanKey[])
        .filter((planKey) => !nextPlans[planKey]);

      if (missingPlanKeys.length) {
        const expectedProductIds = Object.values(APPLE_PRODUCT_IDS_BY_PLAN_KEY);
        throw new Error(
          `App Store products are missing required plans: ${missingPlanKeys.join(', ')}. `
          + `Expected product IDs: ${expectedProductIds.join(', ')}. `
          + `Returned product IDs: ${returnedProductIds.length ? returnedProductIds.join(', ') : '<none>'}.`
        );
      }

      setApplePlans(nextPlans as ApplePlanRecord);
      trackEvent('ios_iap_products_loaded', {
        productCount: products.length,
      });
    } catch (error: unknown) {
      setApplePlans(null);
      setAppleProductsError(true);
      trackEvent('ios_iap_products_failed');
      handleExternalError(toast, error, {
        logPrefix: 'Error loading App Store products:',
      });
    } finally {
      setAppleLoading(false);
    }
  }, [isNativeIos, toast]);

  useEffect(() => {
    if (!isNativeIos) return;
    void loadApplePlans();
  }, [isNativeIos, loadApplePlans]);

  useEffect(() => {
    if (!isNativeIos || !coupleId) return;

    const syncExistingEntitlements = async () => {
      try {
        const entitlements = await getIosCurrentEntitlements();
        if (!entitlements.length) {
          return;
        }

        await syncAppleTransactions(entitlements);
      } catch (error: unknown) {
        handleExternalError(toast, error, {
          logPrefix: 'Error loading current App Store entitlements:',
        });
      }
    };

    void syncExistingEntitlements();
  }, [coupleId, isNativeIos, toast]);

  useEffect(() => {
    if (!plans) return;

    if (!didApplyInitialPlanSelection) {
      const initialPlan = currentPaidPlanKey ?? STRIPE_SUBSCRIPTION_PLAN_KEYS.annual;
      setSelectedPlan(initialPlan);
      previousLivePlanKeyRef.current = currentPaidPlanKey;
      setDidApplyInitialPlanSelection(true);
      return;
    }

    if (currentPaidPlanKey !== previousLivePlanKeyRef.current) {
      previousLivePlanKeyRef.current = currentPaidPlanKey;

      if (currentPaidPlanKey) {
        setSelectedPlan(currentPaidPlanKey);
      }
    }
  }, [currentPaidPlanKey, didApplyInitialPlanSelection, plans]);

  const showAppleBillingCard = isNativeIos;
  const showWebBillingCard = !isNativeIos || isExternalPricingAvailable;
  const billingCardsWrapperClassName =
    showAppleBillingCard && showWebBillingCard
      ? 'mx-auto grid max-w-5xl gap-6 lg:grid-cols-2'
      : 'mx-auto max-w-2xl';

  if (
    loading
    || subscriptionLoading
    || !plans
    || !selectedStripePlanDetails
  ) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 py-2 pb-8">
      <Card className="max-w-2xl mx-auto p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm text-muted-foreground">Current status</p>
            <p className="text-xl font-semibold mt-1">{subscriptionStatusLabel}</p>
            <p className="text-sm text-muted-foreground mt-2">{subscriptionStatusDescription}</p>
            <p className="text-sm mt-2">
              <span className="text-muted-foreground">Current plan:</span>{' '}
              <span className="font-medium text-foreground">
                {currentPlanLabel}
                {currentPriceLabel ? ` (${currentPriceLabel})` : ''}
              </span>
            </p>
            {currentBasePriceLabel && (
              <p className="text-xs text-muted-foreground mt-1">
                Base price: <span className="line-through">{currentBasePriceLabel}</span>
              </p>
            )}
            {status === 'trialing' && usingAppleBilling && (
              <p className="text-sm text-primary mt-2">
                Your Apple introductory offer is currently active.
              </p>
            )}
            {status === 'trialing' && !usingAppleBilling && hasStripeCustomer && (
              <p className="text-sm text-primary mt-2">
                Your active trial will complete first, then billing starts automatically.
              </p>
            )}
            {(() => {
              const msg = deriveSubscriptionDateMessaging({
                subscriptionStatus: status,
                trialEndDate,
                subscriptionEndDate,
                subscriptionRenewalDate,
                subscriptionAutoRenews,
                freeForLife,
              });
              if (!msg) return null;
              return (
                <p className="text-sm text-muted-foreground mt-2">
                  {msg.label}:{' '}
                  <span className="font-medium text-foreground">
                    {new Date(msg.date).toLocaleDateString()}
                  </span>
                </p>
              );
            })()}
          </div>
          <div className="flex min-h-full flex-col items-end gap-2">
            {hasFounderPricing && !isNativeIos && (
              <LifetimeDiscountBadge className="shrink-0 text-[11px]" />
            )}
            {(isPaidActive || freeForLife) && <Check className="h-5 w-5 text-primary mt-1" />}
            {status === 'past_due' && <Crown className="h-5 w-5 text-amber-500 mt-1" />}
          </div>
        </div>
      </Card>

      <div className={billingCardsWrapperClassName}>
        {showWebBillingCard && (
          <Card className="flex flex-col p-8">
            <div className="mb-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Web Billing
              </p>
              <h3 className="mt-2 text-2xl font-bold">Subscribe in your browser</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                {isNativeIos
                  ? 'Charged through our website and managed in Stripe. This option is shown in the iPhone app only when Apple storefront rules allow an external checkout link.'
                  : usingAppleBilling
                    ? 'Your active subscription is managed by Apple. Use the iPhone app to manage that subscription, and contact support if you want help switching billing providers.'
                    : 'Charged through our website and managed in Stripe.'}
              </p>
            </div>

            <div className="mb-6 flex justify-center">
              <ToggleGroup
                type="single"
                variant="outline"
                value={selectedPlan}
                onValueChange={(value) => {
                  if (isCheckoutPlanKey(value)) {
                    setSelectedPlan(value);
                    trackEvent('subscription_plan_selected', {
                      planKey: value,
                      channel: 'web',
                    });
                  }
                }}
              >
                <ToggleGroupItem
                  value={STRIPE_SUBSCRIPTION_PLAN_KEYS.monthly}
                  aria-label="Web monthly plan"
                  disabled={freeForLife || isViewingUser}
                >
                  Monthly
                </ToggleGroupItem>
                <ToggleGroupItem
                  value={STRIPE_SUBSCRIPTION_PLAN_KEYS.annual}
                  aria-label="Web annual plan"
                  disabled={freeForLife || isViewingUser}
                >
                  Annual
                </ToggleGroupItem>
              </ToggleGroup>
            </div>

            <div className="mb-6 text-center">
              <h4 className="mb-2 text-2xl font-bold">{selectedStripePlanDetails.displayName}</h4>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-4xl font-bold">{selectedStripePlanDetails.priceDisplay}</span>
                <span className="text-muted-foreground">/{selectedStripePlanDetails.interval}</span>
              </div>
              {selectedStripePlanDetails.basePriceDisplay && (
                <p className="mt-1 text-sm text-muted-foreground">
                  <span className="line-through">
                    {selectedStripePlanDetails.basePriceDisplay}/{selectedStripePlanDetails.interval}
                  </span>
                </p>
              )}
              {webAnnualMonthlyBreakdown && (
                <p className="mt-2 text-sm text-muted-foreground">
                  Just {webAnnualMonthlyBreakdown} per month
                </p>
              )}
              {selectedStripePlanDetails.discountApplied && hasFounderPricing && (
                <div className="mt-2 flex justify-center">
                  <LifetimeDiscountBadge />
                </div>
              )}
              {selectedPlan === STRIPE_SUBSCRIPTION_PLAN_KEYS.annual
                && selectedStripePlanDetails.yearlySavingsLabel && (
                <p className="mt-2 text-sm font-semibold text-primary">
                  Save {selectedStripePlanDetails.yearlySavingsLabel}
                </p>
              )}
              {status === 'trialing' && formattedTrialEndDate && !usingAppleBilling && (
                <p className="mt-2 text-sm text-muted-foreground">
                  Trial active through {formattedTrialEndDate}
                </p>
              )}
            </div>

            <ul className="mb-8 space-y-3">
              {SUBSCRIPTION_FEATURES.map((feature) => (
                <li key={`web-${feature}`} className="flex items-center gap-2">
                  <Check className="h-5 w-5 text-primary" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <div className="mb-4 mt-auto rounded-md bg-muted/30 p-3 text-xs text-muted-foreground space-y-1">
              {!usingAppleBilling && <p>✓ Manage anytime from Stripe Billing Portal</p>}
              {!usingAppleBilling && !hasStripeCustomer && !isViewingUser && !freeForLife && (
                <p>New web subscriptions start with a {trialDayLabel} free trial.</p>
              )}
              {isNativeIos && !canManageActiveAppleSubscription && (
                <p>
                  Web checkout opens outside the app and should only be used when you prefer
                  browser billing over Apple billing.
                </p>
              )}
              {canManageActiveAppleSubscription && (
                <p>
                  This account already has an active Apple-managed subscription. If you&apos;d
                  like to switch to web billing, please contact{' '}
                  <a href={SUPPORT_MAILTO_URL} className="text-primary hover:underline">
                    {SUPPORT_EMAIL}
                  </a>{' '}
                  and we&apos;ll help.
                </p>
              )}
            </div>

            <p className="mb-4 text-center text-xs text-muted-foreground">
              By subscribing, you agree to our{' '}
              {termsUrl ? (
                <a
                  href={termsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary hover:underline"
                >
                  Terms of Use (EULA)
                </a>
              ) : (
                <span>Terms of Use (EULA)</span>
              )}{' '}
              and{' '}
              {privacyUrl ? (
                <a
                  href={privacyUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary hover:underline"
                >
                  Privacy Policy
                </a>
              ) : (
                <span>Privacy Policy</span>
              )}
              .
            </p>

            <Button
              className="w-full"
              size="lg"
              onClick={webPlanAction.onClick}
              disabled={webPlanAction.disabled || !isSubscriptionManagementAvailable}
            >
              {webPlanAction.loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                webPlanAction.label
              )}
            </Button>

          </Card>
        )}

        {showAppleBillingCard && (
          <Card className="flex flex-col p-8">
            <div className="mb-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Apple In-App Purchase
              </p>
              <h3 className="mt-2 text-2xl font-bold">Subscribe through the App Store</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Charged, renewed, and cancelled by Apple. This section shows App Store pricing only
                and keeps iOS billing separate from browser billing.
              </p>
            </div>

            <div className="mb-6 flex justify-center">
              <ToggleGroup
                type="single"
                variant="outline"
                value={selectedPlan}
                onValueChange={(value) => {
                  if (isCheckoutPlanKey(value)) {
                    setSelectedPlan(value);
                    trackEvent('subscription_plan_selected', {
                      planKey: value,
                      channel: 'ios',
                    });
                  }
                }}
              >
                <ToggleGroupItem
                  value={STRIPE_SUBSCRIPTION_PLAN_KEYS.monthly}
                  aria-label="Apple monthly plan"
                  disabled={freeForLife || isViewingUser}
                >
                  Monthly
                </ToggleGroupItem>
                <ToggleGroupItem
                  value={STRIPE_SUBSCRIPTION_PLAN_KEYS.annual}
                  aria-label="Apple annual plan"
                  disabled={freeForLife || isViewingUser}
                >
                  Annual
                </ToggleGroupItem>
              </ToggleGroup>
            </div>

            <div className="mb-6 text-center">
              {selectedApplePlanDetails ? (
                <>
                  <h4 className="mb-2 text-2xl font-bold">{selectedApplePlanDetails.displayName}</h4>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold">{selectedApplePlanDetails.priceDisplay}</span>
                    <span className="text-muted-foreground">/{selectedApplePlanDetails.interval}</span>
                  </div>
                  {appleAnnualMonthlyBreakdown && (
                    <p className="mt-2 text-sm text-muted-foreground">
                      Just {appleAnnualMonthlyBreakdown} per month
                    </p>
                  )}
                  {status === 'trialing' && formattedTrialEndDate && usingAppleBilling && (
                    <p className="mt-2 text-sm text-muted-foreground">
                      Trial active through {formattedTrialEndDate}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <h4 className="mb-2 text-2xl font-bold">App Store pricing</h4>
                  <p className="text-sm text-muted-foreground">
                    {appleLoading
                      ? 'Loading live App Store pricing...'
                      : 'App Store pricing is temporarily unavailable on this device.'}
                  </p>
                </>
              )}
            </div>

            <ul className="mb-8 space-y-3">
              {SUBSCRIPTION_FEATURES.map((feature) => (
                <li key={`apple-${feature}`} className="flex items-center gap-2">
                  <Check className="h-5 w-5 text-primary" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <div className="mb-4 mt-auto rounded-md bg-muted/30 p-3 text-xs text-muted-foreground space-y-1">
              <p>Apple handles billing, renewals, and cancellations for purchases made in the app.</p>
              <p>Use Restore Purchases if you&apos;ve already subscribed with this Apple ID.</p>
              {hasLiveStripeSubscription && (
                <p>
                  {status === 'trialing'
                    ? 'Starting Apple billing ends the remainder of your web trial immediately.'
                    : 'Starting Apple billing cancels your current web subscription and refunds the unused paid time after Apple confirms the purchase.'}
                </p>
              )}
            </div>

            <p className="mb-4 text-center text-xs text-muted-foreground">
              By subscribing, you agree to our{' '}
              {termsUrl ? (
                <a
                  href={termsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary hover:underline"
                >
                  Terms of Use (EULA)
                </a>
              ) : (
                <span>Terms of Use (EULA)</span>
              )}{' '}
              and{' '}
              {privacyUrl ? (
                <a
                  href={privacyUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary hover:underline"
                >
                  Privacy Policy
                </a>
              ) : (
                <span>Privacy Policy</span>
              )}
              .
            </p>

            {appleProductsError && (
              <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm">
                <p className="font-medium text-foreground">
                  We couldn&apos;t load live App Store pricing.
                </p>
                <p className="mt-1 text-muted-foreground">
                  We&apos;ll keep web pricing separate in the web billing card. Retry once App Store
                  Connect setup has finished propagating.
                </p>
                <Button
                  className="mt-3"
                  variant="outline"
                  onClick={() => {
                    void loadApplePlans();
                  }}
                  disabled={appleLoading || checkingOut !== null}
                >
                  {appleLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  Retry loading Apple pricing
                </Button>
              </div>
            )}

            <Button
              className="w-full"
              size="lg"
              onClick={applePlanAction.onClick}
              disabled={
                applePlanAction.disabled
                || !isSubscriptionManagementAvailable
                || (!canManageActiveAppleSubscription && !selectedApplePlanDetails)
              }
            >
              {applePlanAction.loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                applePlanAction.label
              )}
            </Button>

            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Button
                variant="outline"
                onClick={() => {
                  void handleRestorePurchases();
                }}
                disabled={checkingOut !== null || syncingApple}
              >
                {checkingOut === 'portal' ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCcw className="mr-2 h-4 w-4" />
                )}
                Restore Purchases
              </Button>
            </div>
          </Card>
        )}
      </div>

      <div className="mt-12 space-y-6 max-w-2xl mx-auto">
        <Card className="p-6 bg-muted/50">
          <h3 className="font-semibold mb-4 text-lg">Subscription Details</h3>
          <div className="space-y-4 text-sm text-muted-foreground">
            <div>
              <p className="font-medium text-foreground mb-1">💳 Billing & Cancellation</p>
              <p>
                {isNativeIos
                  ? 'Apple-billed subscriptions purchased in the iPhone app renew automatically and can be managed in your App Store subscriptions settings. If you choose browser billing instead, that subscription is managed separately through Stripe.'
                  : 'Subscriptions renew automatically. Manage billing from Settings → Subscription → Manage in Stripe.'}
              </p>
            </div>
            <div>
              <p className="font-medium text-foreground mb-1">🔄 Refund Policy</p>
              <p>
                {isNativeIos
                  ? 'Apple handles billing and refunds for subscriptions purchased in the app. Web subscriptions follow our standard billing support flow. Contact Apple Support for Apple-billed refunds, or email us at '
                  : 'We offer a 7-day money-back guarantee on all subscriptions. If you are not satisfied within the first 7 days after your trial ends, contact us at '}
                <a href={SUPPORT_MAILTO_URL} className="text-primary hover:underline">{SUPPORT_EMAIL}</a>.
              </p>
            </div>
            <div>
              <p className="font-medium text-foreground mb-1">🎁 Trial & Access</p>
              <p>
                {isNativeIos
                  ? 'If an Apple introductory offer is configured for your subscription, Apple will show those terms during checkout. Access updates automatically after purchase or restore.'
                  : `Your ${trialDayLabel} free trial begins the moment you create your account. After the trial, your chosen plan will automatically start and you'll be charged. You'll receive a reminder email before your trial ends.`}
              </p>
            </div>
            <div>
              <p className="font-medium text-foreground mb-1">📧 Questions?</p>
              <p>
                Need help choosing a plan or have billing questions? Email us at <a href={SUPPORT_MAILTO_URL} className="text-primary hover:underline">{SUPPORT_EMAIL}</a> or visit our <Link to={ROUTES.help} className="text-primary hover:underline">Help Center</Link>.
              </p>
            </div>
          </div>
        </Card>
      </div>

      <p className="mx-auto mt-8 max-w-2xl text-center text-sm text-muted-foreground">
        Your subscription covers both partners.
      </p>
    </div>
  );
};

export default Subscription;
